import bundle from '@sas/reacteua-novacmneua/novacmneua-gui-eua.properties';
import { hsl, parseToHsl } from 'polished';
import { useIsRTL } from '../../../../locale';
import { addBundle } from '../../../../locale/createUseTextLocalizer';
import { useComparator } from '../../../../locale/sorting';
import { useNCTextLocalizer } from '../../../../locale/useNCTextLocalizer';
import { useDesignTokens } from '../../../../theme';
import { postfixTestId } from '../../../../util/testId';
import { Checkbox } from '../../../Checkbox';
import { ColorPickerWithLabels } from '../../../ColorPicker';
import { Flow } from '../../../Layouts/Flow';
import { NumericStepper } from '../../../NumericStepper';
import { Select } from '../../../Select';
import { withLabels } from '../../../withLabels';
import { FocusPreview } from '../../controls/FocusPreview';
import { GlobalSettingsKeys, UI5_NO_COLOR_VALUE } from '../../GlobalSettingsKeys';
import { useSettingsField } from '../../store/SettingsStore';
import cn from './AccessibilityForm.css.json';
import './AccessibilityForm.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
addBundle(bundle);
const NumericStepperWithLabels = withLabels(NumericStepper);
const FocusPreviewWithLabels = withLabels(FocusPreview);
const SelectWithLabels = withLabels(Select);
const keyboardTooltipLocations = ['BottomLeft', 'BottomRight', 'TopLeft', 'TopRight'];
export const AccessibilityForm = () => {
  const localize = useNCTextLocalizer('Settings');
  const isRTL = useIsRTL();
  const formName = 'settings-global-AccessibilityForm';
  const [enableSounds, setEnableSounds] = useSettingsField(GlobalSettingsKeys.PLAY_EARCONS, 'false');
  const [enableVisualEffects, setEnabledVisualEffects] = useSettingsField(GlobalSettingsKeys.PLAY_VISUAL_EFFECTS, 'true');
  const [enablePopupDisplayDuration, setEnablePopupDisplayDuration] = useSettingsField(GlobalSettingsKeys.ENABLE_POPUP_DISPLAY_DURATION, 'false');
  const [popupDisplayDurationValue, setPopupDisplayDurationValue] = useSettingsField(GlobalSettingsKeys.POPUP_DISPLAY_DURATION, '5');
  const [invertApplicationColors, setInvertApplicationColors] = useSettingsField(GlobalSettingsKeys.INVERT_APP_COLORS, 'false');
  const [enableCustomizeFocusIndicator, setEnableCustomizeFocusIndicator] = useSettingsField(GlobalSettingsKeys.FOCUS_OVERRIDE, 'false');
  const [enableKeyboardTooltips, setEnableKeyboardTooltips] = useSettingsField(GlobalSettingsKeys.ENABLE_KEYBOARD_TOOLTIPS, 'false');
  const defaultKeyboardTooltipLocation = isRTL ? 'BottomLeft' : 'BottomRight';
  const [keyboardTooltipLocation, setKeyboardTooltipLocation] = useSettingsField(GlobalSettingsKeys.KEYBOARD_TOOLTIP_LOCATION, defaultKeyboardTooltipLocation);
  const [focusTruncatedHeadings, setFocusTruncatedHeadings] = useSettingsField(GlobalSettingsKeys.FOCUS_TRUNCATED_HEADINGS, 'false');
  const dt = useDesignTokens();
  const focusToken = dt.enableNovaTokens ? {
    bd: dt.tokens['color-action-border-focus'],
    bdWidth: '2px'
  } : dt.baseReportThemeName === 'highcontrast2018' || dt.baseReportThemeName === 'highcontrast2017' ? dt.reportTokens.stateFocus.onLightBg : dt.reportTokens.stateFocus.default;
  const defaultFocusColor = hsl(parseToHsl(focusToken.bd));
  const defaultFocusThickness = focusToken.bdWidth.replace(/px$/, '');
  const [color, setColor] = useSettingsField(GlobalSettingsKeys.FOCUS_COLOR, defaultFocusColor);
  const [thickness, setThickness] = useSettingsField(GlobalSettingsKeys.FOCUS_THICKNESS, defaultFocusThickness);
  const [opacity, setOpacity] = useSettingsField(GlobalSettingsKeys.FOCUS_OPACITY, '100');

  // coerced variables
  const bEnableSounds = enableSounds?.toLocaleLowerCase() === 'true';
  const bEnableVisualEffects = enableVisualEffects?.toLocaleLowerCase() === 'true';
  const bEnablePopupDisplayDuration = enablePopupDisplayDuration?.toLocaleLowerCase() === 'true';
  const bInvertApplicationColors = invertApplicationColors?.toLocaleLowerCase() === 'true';
  const bEnableKeyboardTooltips = enableKeyboardTooltips?.toLocaleLowerCase() === 'true';
  const bEnableCustomizeFocusIndicator = enableCustomizeFocusIndicator?.toLocaleLowerCase() === 'true';
  const bEnableFocusTruncatedHeadings = focusTruncatedHeadings?.toLocaleLowerCase() === 'true';
  const locationSortComparator = useComparator({
    toSortKey: location => localize(`settings.accessibilityForm.keyboardTooltipLocation.${location}.label`),
    direction: 'ascending',
    sensitivity: 'strict'
  });
  const sortedkeyboardTooltipLocations = keyboardTooltipLocations.sort(locationSortComparator);
  return /*#__PURE__*/_jsxs(Flow, {
    orientation: 'vertical',
    spacing: 's',
    className: cn.content,
    children: [/*#__PURE__*/_jsx(Checkbox, {
      testId: postfixTestId(formName, 'enableSounds'),
      label: localize('settings.accessibilityForm.enableSounds.label'),
      helpPopup: {
        library: 'novacmneua-gui-eua',
        contentKey: 'settings.a11yEnableSounds',
        testId: postfixTestId(formName, 'enableSoundsHelpPopup')
      },
      checked: bEnableSounds,
      onPress: () => {
        setEnableSounds((!bEnableSounds).toString());
      }
    }), /*#__PURE__*/_jsx(Checkbox, {
      testId: postfixTestId(formName, 'enableVisualEffects'),
      label: localize('settings.accessibilityForm.enableVisualEffects.label'),
      helpPopup: {
        library: 'novacmneua-gui-eua',
        contentKey: 'settings.a11yEnableVisualEffects',
        testId: postfixTestId(formName, 'enableVisualEffectsHelpPopup')
      },
      checked: bEnableVisualEffects,
      onPress: () => {
        setEnabledVisualEffects((!bEnableVisualEffects).toString());
      }
    }), /*#__PURE__*/_jsx(Checkbox, {
      testId: postfixTestId(formName, 'enablePopupDuration'),
      label: localize('settings.accessibilityForm.popupDurationDisplayCheckbox.label'),
      checked: bEnablePopupDisplayDuration,
      onPress: () => {
        setEnablePopupDisplayDuration((!bEnablePopupDisplayDuration).toString());
      }
    }), /*#__PURE__*/_jsx("div", {
      className: cn.indented,
      children: /*#__PURE__*/_jsx(NumericStepperWithLabels, {
        testId: postfixTestId(formName, 'popupDuration'),
        label: localize('settings.accessibilityForm.popupDurationDisplay.label'),
        suffix: localize('settings.accessibilityForm.popupDurationDisplay.suffix.label'),
        fieldMinWidth: "9.125rem" // avoid overlapping between the numeric stepper and its suffix when the space for numeric stepper is reducing
        ,
        disabled: !bEnablePopupDisplayDuration,
        value: popupDisplayDurationValue,
        minimum: 5,
        maximum: 50,
        labelPosition: "left",
        onChange: event => {
          setPopupDisplayDurationValue(event.displayValue, true);
        },
        onCommit: event => {
          if (event.value !== null && event.value !== undefined) {
            setPopupDisplayDurationValue(event.value.toString(), false);
          }
        }
      })
    }), /*#__PURE__*/_jsx(Checkbox, {
      testId: postfixTestId(formName, 'invertApplicationColors'),
      label: localize('settings.accessibilityForm.invertApplicationColors.label'),
      checked: bInvertApplicationColors,
      onPress: () => {
        setInvertApplicationColors((!bInvertApplicationColors).toString());
      }
    }), /*#__PURE__*/_jsx(Checkbox, {
      testId: postfixTestId(formName, 'enableKeyboardTooltips'),
      label: localize('settings.accessibilityForm.enableKeyboardTooltips.label'),
      checked: bEnableKeyboardTooltips,
      onPress: () => {
        setEnableKeyboardTooltips((!bEnableKeyboardTooltips).toString());
      }
    }), /*#__PURE__*/_jsx(SelectWithLabels, {
      testId: postfixTestId(formName, 'keyboardTooltipLocation'),
      className: cn.indented,
      labelPosition: "top",
      label: localize('settings.accessibilityForm.keyboardTooltipLocation.label'),
      disabled: !bEnableKeyboardTooltips,
      selectedIndex: sortedkeyboardTooltipLocations.indexOf(keyboardTooltipLocation ?? defaultKeyboardTooltipLocation),
      values: sortedkeyboardTooltipLocations.map(location => ({
        text: localize(`settings.accessibilityForm.keyboardTooltipLocation.${location}.label`)
      })),
      onSelected: selectedIndex => {
        setKeyboardTooltipLocation(sortedkeyboardTooltipLocations[selectedIndex ?? 0]);
      }
    }), /*#__PURE__*/_jsx(Checkbox, {
      testId: postfixTestId(formName, 'focusTruncatedHeading'),
      className: cn.indented,
      label: localize('settings.accessibilityForm.focusTruncatedHeadingCheckbox.label'),
      description: localize('settings.accessibilityForm.focusTruncatedHeadingCheckbox.description.label'),
      disabled: !bEnableKeyboardTooltips,
      checked: bEnableFocusTruncatedHeadings,
      onPress: () => {
        setFocusTruncatedHeadings((!bEnableFocusTruncatedHeadings).toString());
      }
    }), /*#__PURE__*/_jsx(Checkbox, {
      testId: postfixTestId(formName, 'enableCustomFocus'),
      label: localize('settings.accessibilityForm.customizeFocusIndicator.label'),
      checked: bEnableCustomizeFocusIndicator,
      onPress: () => {
        setEnableCustomizeFocusIndicator((!bEnableCustomizeFocusIndicator).toString());
      }
    }), /*#__PURE__*/_jsxs(Flow, {
      orientation: 'vertical',
      className: cn.indented,
      children: [/*#__PURE__*/_jsx(ColorPickerWithLabels, {
        testId: postfixTestId(formName, 'customFocusColor'),
        selectedColor: {
          color: color === UI5_NO_COLOR_VALUE ? defaultFocusColor : color
        },
        onColorChange: color => {
          setColor(color.color);
        },
        label: localize('settings.accessibilityForm.colorpicker.label'),
        disabled: !bEnableCustomizeFocusIndicator,
        labelPosition: "left",
        fieldLabelWidth: "6rem",
        hideNoColor: true,
        hideAutomaticColor: true
      }), /*#__PURE__*/_jsx(NumericStepperWithLabels, {
        testId: postfixTestId(formName, 'customFocusThickness'),
        label: localize('settings.accessibilityForm.thickness.label'),
        suffix: localize('settings.accessibilityForm.thickness.suffix.label'),
        fieldMinWidth: "8.625rem" // avoid overlapping between the numeric stepper and its suffix when the space for numeric stepper is reducing
        ,
        disabled: !bEnableCustomizeFocusIndicator,
        labelPosition: "left",
        fieldLabelWidth: "6rem",
        value: thickness,
        minimum: 1,
        maximum: 5,
        onCommit: event => {
          if (event.value !== null && event.value !== undefined) {
            setThickness(event.value.toString(), false);
          }
        },
        onChange: event => {
          setThickness(event.displayValue, true);
        }
      }), /*#__PURE__*/_jsx(NumericStepperWithLabels, {
        testId: postfixTestId(formName, 'customFocusOpacity'),
        label: localize('settings.accessibilityForm.opacity.label'),
        suffix: localize('settings.accessibilityForm.opacity.suffix.label'),
        fieldMinWidth: "9.625rem" // avoid overlapping between the numeric stepper and its suffix when the space for numeric stepper is reducing
        ,
        disabled: !bEnableCustomizeFocusIndicator,
        labelPosition: "left",
        fieldLabelWidth: "6rem",
        value: opacity,
        minimum: 0,
        maximum: 100,
        onChange: event => {
          setOpacity(event.displayValue, true);
        },
        onCommit: event => {
          if (event.value !== null && event.value !== undefined) {
            setOpacity(event.value.toString(), false);
          }
        }
      }), /*#__PURE__*/_jsx(FocusPreviewWithLabels, {
        label: localize('settings.accessibilityForm.preview.label'),
        color: color === UI5_NO_COLOR_VALUE ? defaultFocusColor : color,
        opacity: isOpacityValueValid(opacity) ? parseInt(opacity) : 100,
        thickness: isThicknessValueValid(thickness) ? parseInt(thickness) : 1,
        disabled: !bEnableCustomizeFocusIndicator
      })]
    })]
  });
};
function isThicknessValueValid(value) {
  return parseInt(value) >= 1 && parseInt(value) <= 5;
}
function isOpacityValueValid(value) {
  return parseInt(value) >= 0 && parseInt(value) <= 100;
}
//# sourceMappingURL=AccessibilityForm.js.map